import { useAuthenticator } from "@aws-amplify/ui-react";
import { useState, useMemo } from "react";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import { useQuery } from "@tanstack/react-query";
import SingleSelect from "./SingleSelect";
import { listTableData } from "../utilities/request";
import {
  columnOrder,
  selectColumnOrder,
  speakingTableColumns,
  seasons,
  sortSeasonsDescending,
} from "../customConfig";
import { Box, Button } from "@mui/material";
import RecordingStack from "./RecordingStack";
import RenderPartOneTopic from "./Render/RenderPartOneTopic";
import RenderPartTwoAndThree from "./Render/RenderPartTwoAndThree";

interface BasicTableProps {
  selectedUser: string | null;
  handleSelect?: (questions: any[]) => void;
  type?: string;
}

const BasicTable: React.FC<BasicTableProps> = ({
  selectedUser,
  handleSelect,
  type,
}) => {
  const { user } = useAuthenticator();
  const [typeName, setTypeName] = useState<any>(type || "PartOneTopic");

  // Sorted seasons from latest to oldest (e.g. 2026-3, 2026-2, ..., 2025-1)
  const defaultSortedSeasons = useMemo(() => sortSeasonsDescending(seasons), []);
  const [selectedSeason, setSelectedSeason] = useState<string>(
    defaultSortedSeasons[0] || "2026-3"
  );

  const { data, isError, isFetching, isLoading } = useQuery({
    queryKey: [typeName],
    queryFn: () => listTableData(typeName),
    select: (data) => {
      switch (typeName) {
        case "PartOneTopic":
          return data.data.listPartOneTopics.items;
        case "PartOne":
          return data.data.listPartOnes.items;
        case "PartTwo":
          return data.data.listPartTwos.items;
        case "PartThree":
          return data.data.listPartThrees.items;
        default:
          return [];
      }
    },
  });

  // Collect all available seasons dynamically from config and fetched data, sorted descending
  const seasonOptions = useMemo(() => {
    const seasonSet = new Set<string>(seasons);
    if (data && Array.isArray(data)) {
      data.forEach((item: any) => {
        if (Array.isArray(item?.season)) {
          item.season.forEach((s: string) => {
            if (s && typeof s === "string") seasonSet.add(s);
          });
        } else if (typeof item?.season === "string" && item.season) {
          seasonSet.add(item.season);
        }
      });
    }
    const sorted = sortSeasonsDescending(Array.from(seasonSet));
    return [...sorted, "All"];
  }, [data]);

  // Filter data by selectedSeason (defaulting to the latest season)
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (selectedSeason === "All") return data;
    return data.filter((row: any) => {
      if (!row.season) return false;
      if (Array.isArray(row.season)) {
        return row.season.includes(selectedSeason);
      }
      return row.season === selectedSeason;
    });
  }, [data, selectedSeason]);

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => speakingTableColumns[typeName as keyof typeof speakingTableColumns],
    [typeName]
  );

  const isSelect = !!handleSelect;
  return (
    <MaterialReactTable
      columns={columns}
      data={filteredData}
      enableRowSelection={!!handleSelect}
      enableExpandAll={false}
      muiToolbarAlertBannerProps={
        isError
          ? {
              color: "error",
              children: "Error loading data",
            }
          : undefined
      }
      renderTopToolbarCustomActions={({ table }) => {
        const handleSumbit = () => {
          const selectedQuestionId = table
            .getSelectedRowModel()
            .flatRows.map((row) => {
              return row.original.id;
            });
          if (isSelect) {
            handleSelect?.(selectedQuestionId);
          }
        };
        return (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <SingleSelect
              options={["PartOneTopic", "PartOne", "PartTwo", "PartThree"]}
              label="Type"
              value={typeName}
              setValue={setTypeName}
              disabled={!!type}
            />
            <SingleSelect
              options={seasonOptions}
              label="Season"
              value={selectedSeason}
              setValue={setSelectedSeason}
            />
            {isSelect && (
              <Button
                color="primary"
                disabled={!table.getIsSomeRowsSelected()}
                onClick={handleSumbit}
                variant="contained"
              >
                submit
              </Button>
            )}
          </Box>
        );
      }}
      renderDetailPanel={({ row }) => {
        const student = selectedUser || user.username;
        if (row.getIsExpanded() && student) {
          const { typeName, id } = row.original;
          if (["PartOne", "PartTwo", "PartThree"].includes(typeName)) {
            return (
              <RecordingStack
                typeName={typeName}
                id={id}
                student={student}
                subQuestions={row.original.subQuestions}
              />
            );
          } else if (typeName === "PartOneTopic") {
            return (
              <RenderPartOneTopic topic={row.original} student={student} />
            );
          } else {
            return (
              <RenderPartTwoAndThree student={student} p2={row.original} />
            );
          }
        }
      }}
      positionExpandColumn="last"
      state={{
        isLoading,
        showAlertBanner: isError,
        showProgressBars: isFetching,
        columnOrder: isSelect
          ? selectColumnOrder[typeName as keyof typeof columnOrder]
          : columnOrder[typeName as keyof typeof columnOrder],
      }}
    />
  );
};

export default BasicTable;
